import type {
  WazuhAgentOverviewData,
  WazuhEndpointActionRequiredItem,
  WazuhMostVulnerableEndpointItem,
  WazuhOrganizationAttentionItem,
  WazuhPortfolioHealthResellerData,
  WazuhPortfolioHealthSubOrgData,
  WazuhRecentIncidentItem,
  WazuhSecurityScoreData,
} from 'src/types/mindtime/wazuhDashboard';
import type {
  WazuhResellerOverviewState,
  WazuhSubOrgOverviewState,
} from '../shared/useWazuhOverviewData';
import { previewPaged } from './previewUtils';

const previewSecurityScore = (): WazuhSecurityScoreData => ({
  score: 78,
  vsAverage: 4,
  impacts: [
    { factor: 'Critical vulnerabilities', impactPercent: -6 },
    { factor: 'Endpoint coverage', impactPercent: 8 },
  ],
});

const previewAgentOverview = (): WazuhAgentOverviewData => ({
  agentStatus: {
    active: 22,
    warning: 3,
    offline: 2,
  },
  versionDistribution: [
    { version: '4.7.0', count: 14 },
    { version: '4.6.2', count: 10 },
  ],
  dataIngestion: {
    eventsCollected: 1250000,
    alertsGenerated: 342,
    logSources: 18,
    avgEventsPerSecond: 42,
  },
});

const previewOrganizationsAttention = () =>
  previewPaged<WazuhOrganizationAttentionItem>(
    [
      {
        organizationId: 'preview-org-3',
        organizationName: 'Nexus Dynamics',
        securityScore: 68,
        criticalAlertCount: 5,
        endpointCount: 15,
        openIssueCount: 9,
        mainIssue: 'Critical vulnerabilities detected',
        mainIssueSeverity: 'critical',
      },
      {
        organizationId: 'preview-org-2',
        organizationName: 'Vertex Solutions',
        securityScore: 76,
        criticalAlertCount: 2,
        endpointCount: 8,
        openIssueCount: 6,
        mainIssue: 'Malware detection pending review',
        mainIssueSeverity: 'high',
      },
    ],
    0,
    5,
  );

const previewEndpointsActionRequired = () =>
  previewPaged<WazuhEndpointActionRequiredItem>(
    [
      {
        systemWazuhAgentId: 'preview-endpoint-3',
        endpoint: 'APP-SERVER-01',
        hostName: 'app-server-01.nexus.local',
        organization: 'Nexus Dynamics',
        issue: 'Critical vulnerabilities detected',
        issueLabel: 'Critical',
        lastSeen: '2026-08-28T08:30:00.000Z',
      },
      {
        systemWazuhAgentId: 'preview-endpoint-2',
        endpoint: 'HR-PC-17',
        hostName: 'hr-pc-17.vertex.local',
        organization: 'Vertex Solutions',
        issue: 'Malware detection pending review',
        issueLabel: 'High',
        lastSeen: '2026-08-28T09:42:00.000Z',
      },
    ],
    0,
    5,
  );

const previewRecentIncidents = () =>
  previewPaged<WazuhRecentIncidentItem>(
    [
      {
        incident: 'Encoded PowerShell execution',
        time: '2026-08-28T10:15:00.000Z',
        client: 'Acme Financial',
        endpoint: 'FIN-SRV-02',
        hostName: 'fin-srv-02.acme.local',
      },
      {
        incident: 'Credential dumping attempt',
        time: '2026-08-28T09:42:00.000Z',
        client: 'Vertex Solutions',
        endpoint: 'HR-PC-17',
        hostName: 'hr-pc-17.vertex.local',
      },
    ],
    0,
    5,
  );

const previewMostVulnerableEndpoints = () =>
  previewPaged<WazuhMostVulnerableEndpointItem>(
    [
      {
        systemWazuhAgentId: 'preview-endpoint-3',
        endpoint: 'APP-SERVER-01',
        hostName: 'app-server-01.nexus.local',
        organizationId: 'preview-org-3',
        vulnerabilityCount: 14,
        severity: 'critical',
      },
      {
        systemWazuhAgentId: 'preview-endpoint-2',
        endpoint: 'HR-PC-17',
        hostName: 'hr-pc-17.vertex.local',
        organizationId: 'preview-org-2',
        vulnerabilityCount: 8,
        severity: 'high',
      },
    ],
    0,
    5,
  );

export const getResellerDashboardPreviewState = (): WazuhResellerOverviewState => ({
  loading: false,
  portfolioHealth: {
    myClients: {
      total: 4,
      healthy: 2,
      needAttention: 2,
    },
    protectedEndpoints: {
      total: 41,
      online: 38,
      issues: 3,
      deltaThisMonth: 5,
    },
    criticalIncidents: {
      total: 7,
      unresolvedOver48h: 2,
      todayCount: 3,
    },
    openTasks: {
      total: 12,
      deltaSinceYesterday: 2,
      highPriority: 4,
    },
  } satisfies WazuhPortfolioHealthResellerData,
  agentOverview: previewAgentOverview(),
  securityScore: previewSecurityScore(),
  organizationsAttention: previewOrganizationsAttention(),
  endpointsActionRequired: previewEndpointsActionRequired(),
  recentIncidents: previewRecentIncidents(),
});

export const getSubOrgDashboardPreviewState = (): WazuhSubOrgOverviewState => ({
  loading: false,
  portfolioHealth: {
    connectedClients: {
      total: 12,
      thisMonth: 2,
    },
    activeThreats: {
      total: 3,
      today: 1,
      percentageIncrease: 12,
    },
    openVulnerabilities: {
      total: 14,
      today: 2,
      percentageIncrease: 8,
    },
    backupStatus: {
      status: 'Healthy',
      lastBackup: '2026-08-28T06:00:00.000Z',
    },
  } satisfies WazuhPortfolioHealthSubOrgData,
  agentOverview: previewAgentOverview(),
  securityScore: previewSecurityScore(),
  organizationsAttention: previewOrganizationsAttention(),
  endpointsActionRequired: previewEndpointsActionRequired(),
  recentIncidents: previewRecentIncidents(),
  mostVulnerableEndpoints: previewMostVulnerableEndpoints(),
});
